<template>
  <v-container fluid class="py-6 px-4">
    <v-card class="elevation-3">
      <!-- Header -->
      <v-toolbar dark :color="selected.length > 0 ? 'orange darken-2' : 'primary'" class="elevation-0">
        <v-icon left>local_shipping</v-icon>
        <v-toolbar-title class="font-weight-medium">
          {{ selected.length > 0 ? `${selected.length} transportista(s) seleccionado(s)` : 'Gestión de Transportistas' }}
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Acciones en Lote -->
        <template v-if="selected.length > 0">
          <v-btn small flat color="white" @click="bulkActivate" :loading="bulkActionLoading">
            <v-icon small left>check_circle</v-icon>
            Activar
          </v-btn>
          <v-btn small flat color="white" @click="bulkDeactivate" :loading="bulkActionLoading">
            <v-icon small left>block</v-icon>
            Desactivar
          </v-btn>
          <v-btn small flat color="white" @click="bulkDelete" :loading="bulkActionLoading">
            <v-icon small left>delete</v-icon>
            Eliminar
          </v-btn>
        </template>

        <template v-else>
          <v-btn outline color="white" @click="openCreateDialog">
            <v-icon left>add</v-icon>
            Nuevo Transportista
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn icon dark v-on="on">
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-tile @click="loadStatistics">
                <v-list-tile-action>
                  <v-icon>trending_up</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Ver Estadísticas</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-menu>
        </template>
      </v-toolbar>

      <!-- Búsqueda y Filtros -->
      <v-card-text class="pa-4 grey lighten-4 border-bottom">
        <v-layout row wrap align-center>
          <v-flex xs12 sm6 md4 class="pr-3">
            <v-text-field
              v-model="searchQuery"
              label="Buscar transportista..."
              color="primary"
              prepend-icon="search"
              clearable
              hide-details
              hint="Busca por nombre, email, teléfono o RFC"
              @input="handleSearch"
            ></v-text-field>
          </v-flex>

          <v-flex xs12 sm6 md2 class="pr-3">
            <v-select
              v-model="filterStatus"
              :items="[
                { text: 'Todos', value: null },
                { text: 'Activos', value: true },
                { text: 'Inactivos', value: false }
              ]"
              item-text="text"
              item-value="value"
              label="Estado"
              color="primary"
              prepend-icon="filter_list"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>

          <v-flex xs12 sm6 md3 class="pr-3">
            <v-select
              v-model="sortBy"
              :items="[
                { text: 'Nombre', value: 'name' },
                { text: 'Fecha', value: 'created_at' },
                { text: 'Embarques', value: 'shipment_count' }
              ]"
              item-text="text"
              item-value="value"
              label="Ordenar por"
              color="primary"
              prepend-icon="sort"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>

          <v-flex xs12 sm6 md3>
            <v-select
              v-model="sortOrder"
              :items="[
                { text: 'Ascendente', value: 'asc' },
                { text: 'Descendente', value: 'desc' }
              ]"
              item-text="text"
              item-value="value"
              label="Orden"
              color="primary"
              prepend-icon="swap_vert"
              hide-details
              @change="handleFilterChange"
            ></v-select>
          </v-flex>
        </v-layout>
      </v-card-text>

      <!-- Tabla de Transportistas -->
      <v-data-table
        v-model="selected"
        select-all
        item-key="id"
        :headers="headers"
        :items="carriers"
        :loading="loading"
        :total-items="totalCarriers"
        :pagination.sync="pagination"
        :rows-per-page-items="[10, 25, 50, { text: 'Todos', value: -1 }]"
        rows-per-page-text="Filas por página"
        no-data-text="No hay transportistas registrados"
        class="elevation-0"
      >
        <template v-slot:items="props">
          <td>
            <v-checkbox v-model="props.selected" primary hide-details></v-checkbox>
          </td>
          <td>{{ props.item.name }}</td>
          <td class="grey--text text--darken-1">{{ props.item.email || '—' }}</td>
          <td>{{ props.item.phone || '—' }}</td>
          <td class="text-xs-center">
            <v-chip :color="props.item.is_active ? 'green' : 'grey'" text-color="white" small>
              {{ props.item.is_active ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </td>
          <td class="text-xs-center">{{ props.item.shipment_count }}</td>
          <td class="text-xs-center">
            <span v-if="props.item.last_shipment_date" class="caption">
              {{ formatDate(props.item.last_shipment_date) }}
            </span>
            <span v-else class="caption grey--text">Sin envíos</span>
          </td>
          <td class="text-xs-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small class="mr-2" color="primary" v-on="on" @click="editCarrier(props.item)">edit</v-icon>
              </template>
              <span>Editar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small class="mr-2" :color="props.item.is_active ? 'orange darken-2' : 'green'" v-on="on" @click="toggleCarrierStatus(props.item)">
                  {{ props.item.is_active ? 'block' : 'check_circle' }}
                </v-icon>
              </template>
              <span>{{ props.item.is_active ? 'Desactivar' : 'Activar' }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small color="red" v-on="on" @click="deleteCarrier(props.item)">delete</v-icon>
              </template>
              <span>Eliminar</span>
            </v-tooltip>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="text-xs-center pa-5">
            <v-icon size="60" color="grey lighten-1" class="mb-2">local_shipping</v-icon>
            <p class="grey--text">No hay transportistas registrados</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Diálogo Crear/Editar -->
    <v-dialog v-model="showDialog" max-width="560px" persistent scrollable>
      <v-card>
        <v-toolbar dark color="primary">
          <v-icon left>{{ editingCarrier.id ? 'edit' : 'add' }}</v-icon>
          <v-toolbar-title>{{ editingCarrier.id ? 'Editar Transportista' : 'Crear Transportista' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="editingCarrier.name"
              label="Nombre del Transportista *"
              color="primary"
              prepend-icon="person"
              maxlength="100"
              :rules="[v => !!v || 'El nombre es requerido']"
              :error-messages="errors.name"
              @input="clearError('name')"
            ></v-text-field>

            <v-text-field
              v-model="editingCarrier.contact_name"
              label="Nombre de Contacto"
              color="primary"
              prepend-icon="badge"
              maxlength="200"
              :error-messages="errors.contact_name"
              @input="clearError('contact_name')"
            ></v-text-field>

            <v-text-field
              v-model="editingCarrier.email"
              label="Email"
              type="email"
              color="primary"
              prepend-icon="alternate_email"
              :error-messages="errors.email"
              @input="clearError('email')"
            ></v-text-field>

            <v-text-field
              v-model="editingCarrier.phone"
              label="Teléfono"
              color="primary"
              prepend-icon="phone"
              :error-messages="errors.phone"
              @input="clearError('phone')"
            ></v-text-field>

            <v-text-field
              v-model="editingCarrier.company_name"
              label="Nombre de Empresa"
              color="primary"
              prepend-icon="business"
              maxlength="200"
              :error-messages="errors.company_name"
              @input="clearError('company_name')"
            ></v-text-field>

            <v-text-field
              v-model="editingCarrier.rfc"
              label="RFC (Opcional)"
              color="primary"
              prepend-icon="assignment"
              maxlength="20"
              :error-messages="errors.rfc"
              @input="clearError('rfc')"
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4 border-top">
          <v-spacer></v-spacer>
          <v-btn flat color="secondary" @click="closeDialog" :disabled="savingCarrier">
            Cancelar
          </v-btn>
          <v-btn color="primary" :loading="savingCarrier" @click="saveCarrier">
            <v-icon left>{{ editingCarrier.id ? 'save' : 'add' }}</v-icon>
            {{ editingCarrier.id ? 'Actualizar' : 'Crear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo Confirmar Eliminación -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline error--text">
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          <p class="mb-2">¿Desactivar el transportista <strong>{{ carrierToDelete && carrierToDelete.name }}</strong>?</p>
          <p class="caption grey--text">Esta acción desactivará el registro pero no lo eliminará del sistema.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat color="secondary" @click="showDeleteDialog = false" :disabled="deletingCarrier">
            Cancelar
          </v-btn>
          <v-btn color="error" :loading="deletingCarrier" @click="confirmDelete">
            Desactivar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar de Notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" bottom right>
      {{ snackbar.message }}
      <v-btn flat color="white" @click="snackbar.show = false">
        Cerrar
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'CarriersManagement',
  data() {
    return {
      carriers: [],
      totalCarriers: 0,
      loading: false,
      searchQuery: '',
      filterStatus: null,
      sortBy: 'name',
      sortOrder: 'asc',

      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },

      headers: [
        { text: 'Nombre', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Teléfono', value: 'phone' },
        { text: 'Estado', value: 'is_active', align: 'center', sortable: false },
        { text: 'Envíos', value: 'shipment_count', align: 'center' },
        { text: 'Último Envío', value: 'last_shipment_date', align: 'center' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false }
      ],

      selected: [],
      bulkActionLoading: false,

      showDialog: false,
      editingCarrier: this.getEmptyCarrier(),
      valid: true,
      savingCarrier: false,
      errors: {},

      showDeleteDialog: false,
      carrierToDelete: null,
      deletingCarrier: false,

      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  watch: {
    pagination: {
      handler() {
        this.loadCarriers()
      },
      deep: true
    }
  },
  mounted() {
    this.loadCarriers()
  },
  methods: {
    getEmptyCarrier() {
      return {
        id: null,
        name: '',
        contact_name: '',
        email: '',
        phone: '',
        company_name: '',
        rfc: ''
      }
    },
    async loadCarriers() {
      this.loading = true
      try {
        const pageSize = this.pagination.rowsPerPage === -1 ? 1000 : this.pagination.rowsPerPage
        const params = new URLSearchParams({
          page: this.pagination.page,
          page_size: pageSize,
          sort_by: this.sortBy,
          order: this.sortOrder
        })

        if (this.searchQuery) {
          params.append('q', this.searchQuery)
        }
        if (this.filterStatus !== null) {
          params.append('is_active', this.filterStatus)
        }

        const response = await fetch(`https://empresa-service.intrare.app/shipments/advanced/carriers/search/?${params}`, {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) throw new Error('Error al cargar transportistas')

        const data = await response.json()
        this.carriers = data.results || []
        this.totalCarriers = data.count || 0
      } catch (error) {
        this.showSnackbar('Error al cargar los transportistas', 'error')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.loadCarriers()
    },
    handleFilterChange() {
      this.pagination.page = 1
      this.loadCarriers()
    },
    openCreateDialog() {
      this.editingCarrier = this.getEmptyCarrier()
      this.errors = {}
      this.showDialog = true
    },
    editCarrier(carrier) {
      this.editingCarrier = { ...carrier }
      this.errors = {}
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
      this.editingCarrier = this.getEmptyCarrier()
      this.errors = {}
    },
    async saveCarrier() {
      if (this.$refs.form && !this.$refs.form.validate()) return

      this.savingCarrier = true
      this.errors = {}

      try {
        const method = this.editingCarrier.id ? 'PUT' : 'POST'
        const url = this.editingCarrier.id
          ? `https://empresa-service.intrare.app/shipments/advanced/carriers/${this.editingCarrier.id}/`
          : 'https://empresa-service.intrare.app/shipments/advanced/carriers/'

        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: this.editingCarrier.name,
            contact_name: this.editingCarrier.contact_name,
            email: this.editingCarrier.email,
            phone: this.editingCarrier.phone,
            company_name: this.editingCarrier.company_name,
            rfc: this.editingCarrier.rfc
          })
        })

        const data = await response.json()

        if (!response.ok) {
          this.errors = data
          throw new Error('Error al guardar')
        }

        this.showSnackbar(
          this.editingCarrier.id
            ? 'Transportista actualizado exitosamente'
            : 'Transportista creado exitosamente',
          'success'
        )
        this.closeDialog()
        this.loadCarriers()
      } catch (error) {
        if (!Object.keys(this.errors).length) {
          this.showSnackbar('Error al guardar el transportista', 'error')
        }
      } finally {
        this.savingCarrier = false
      }
    },
    deleteCarrier(carrier) {
      this.carrierToDelete = carrier
      this.showDeleteDialog = true
    },
    async confirmDelete() {
      this.deletingCarrier = true

      try {
        const response = await fetch(`https://empresa-service.intrare.app/shipments/advanced/carriers/${this.carrierToDelete.id}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) throw new Error('Error al eliminar')

        this.showSnackbar('Transportista desactivado exitosamente', 'success')
        this.showDeleteDialog = false
        this.carrierToDelete = null
        this.loadCarriers()
      } catch (error) {
        this.showSnackbar('Error al desactivar el transportista', 'error')
      } finally {
        this.deletingCarrier = false
      }
    },
    async toggleCarrierStatus(carrier) {
      const endpoint = carrier.is_active ? 'deactivate' : 'activate'

      try {
        const response = await fetch(
          `https://empresa-service.intrare.app/shipments/advanced/carriers/${carrier.id}/${endpoint}/`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Token ${this.$store.getters.getToken}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) throw new Error('Error al cambiar estado')

        this.showSnackbar(
          carrier.is_active
            ? 'Transportista desactivado'
            : 'Transportista activado',
          'success'
        )
        this.loadCarriers()
      } catch (error) {
        this.showSnackbar('Error al cambiar el estado', 'error')
      }
    },
    async exportToCSV() {
      try {
        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/catalog-export/carriers-csv/', {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`
          }
        })

        if (!response.ok) throw new Error('Error al exportar')

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `transportistas-${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        this.showSnackbar('Archivo exportado exitosamente', 'success')
      } catch (error) {
        this.showSnackbar('Error al exportar los datos', 'error')
      }
    },
    async loadStatistics() {
      try {
        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/carriers/statistics/', {
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`
          }
        })

        if (!response.ok) throw new Error('Error al cargar estadísticas')

        const stats = await response.json()
        this.showSnackbar(
          `Total: ${stats.total} | Activos: ${stats.active} | Inactivos: ${stats.inactive}`,
          'info'
        )
      } catch (error) {
        this.showSnackbar('Error al cargar las estadísticas', 'error')
      }
    },
    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field)
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },
    async bulkActivate() {
      await this.executeBulkAction('activate')
    },
    async bulkDeactivate() {
      await this.executeBulkAction('deactivate')
    },
    async bulkDelete() {
      await this.executeBulkAction('delete')
    },
    async executeBulkAction(action) {
      this.bulkActionLoading = true

      try {
        const carrierIds = this.selected.map(c => c.id)

        const response = await fetch('https://empresa-service.intrare.app/shipments/advanced/carriers/bulk_action/', {
          method: 'POST',
          headers: {
            'Authorization': `Token ${this.$store.getters.getToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            carrier_ids: carrierIds,
            action: action,
            reason: 'Acción en lote desde panel de administración'
          })
        })

        const data = await response.json()

        if (!response.ok) throw new Error(data.message || 'Error en acción en lote')

        this.showSnackbar(data.message || `${data.updated_count} transportista(s) actualizado(s)`, 'success')
        this.selected = []
        this.loadCarriers()
      } catch (error) {
        this.showSnackbar(error.message || 'Error al ejecutar acción en lote', 'error')
      } finally {
        this.bulkActionLoading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}
</style>
